import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import * as fbs from "@/api/fbs";
import { Card, CardContent } from "@/components/ui/card";
import { supplyStatusLabel } from "@/lib/fbs";
import { LoadState, useGoBack } from "@/lib/scan-ui/screen";

/** Поставки кабинета — путь назад к уже уехавшей.
 *
 * ⚠️ Нужен потому, что главный экран показывает только ОТКРЫТУЮ поставку:
 * переданная в доставку уходит с него совсем, а доделать по ней остаётся
 * многое — «Разбор» и документы 1С. 15.09.2026 такую поставку искали запросом
 * в базу. */
export function FbsSuppliesPage(): React.ReactElement {
  const navigate = useNavigate();
  const goBack = useGoBack("/fbs");
  const { cabinetId = "" } = useParams();
  const cabinet = Number(cabinetId);
  const q = useQuery({
    queryKey: ["fbs-supplies", cabinet],
    queryFn: () => fbs.listSupplies(cabinet),
    enabled: Number.isFinite(cabinet) && cabinet > 0,
  });

  return (
    <div className="mx-auto max-w-2xl space-y-3">
      <div className="flex items-center gap-2">
        <button type="button" onClick={goBack} className="text-sm text-muted-foreground">
          ← Назад
        </button>
        <h1 className="text-xl font-semibold">Поставки</h1>
      </div>

      {q.isLoading || q.error ? (
        <LoadState
          isLoading={q.isLoading}
          error={q.error}
          onRetry={() => q.refetch()}
          className="flex flex-col items-center justify-center gap-3 rounded-xl border p-6 text-center"
        />
      ) : (
        ((rows) =>
          rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              По этому кабинету поставок пока нет.
            </p>
          ) : (
            <div className="space-y-2">
              {rows.map((r) => (
                <Card
                  key={r.supply_id}
                  className="cursor-pointer transition-colors hover:bg-accent"
                  onClick={() => navigate(`/fbs/supplies/${r.supply_id}/finish`)}
                >
                  <CardContent className="flex items-center gap-3 p-3">
                    <div className="flex-1">
                      <div className="font-medium">
                        {r.wb_supply_id}
                        {r.is_b2b && " · B2B"}
                        {r.warehouse_name && ` · ${r.warehouse_name}`}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleString("ru-RU", {
                          day: "2-digit",
                          month: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        {" · "}
                        {supplyStatusLabel(r.status)} · {r.picks} шт
                      </div>
                      {/* Показываем именно то, ради чего сюда возвращаются:
                          что осталось доделать. */}
                      <div className="mt-1 flex gap-2 text-xs">
                        {r.issues > 0 && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-900">
                            в разборе {r.issues}
                          </span>
                        )}
                        {r.documents === 0 ? (
                          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-700">
                            документов нет
                          </span>
                        ) : (
                          <span className="text-muted-foreground">
                            документов {r.documents}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ))(q.data ?? [])
      )}
    </div>
  );
}
